import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import Button from '../../../components/ui/Button'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormDateField from '../../../components/ui/form/FormDateField'
import FormTimeRangeField from '../../../components/ui/form/FormTimeRangeField'
import { useToast } from '../../../components/ui/ToastProvider'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { regexField, requiredText } from '../../../lib/forms/rules'
import ParticipantsSection from '../../minutes/components/ParticipantsSection'
import type { EmployeePick } from '../../minutes/types'
import { toPersonInput } from '../../minutes/personInput'
import { useUpdateMeeting } from '../hooks/useUpdateMeeting'
import { type MeetingDetail, type MeetingParticipant } from '../types'
import { COLUMN_BYTES } from '../../../lib/columnBytes'

/** 24 saat biçimi SS:DD (00:00–23:59) — create şemasıyla aynı. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

const editSchema = z
  .object({
    name: requiredText(250, COLUMN_BYTES.meetingName),
    meetingDate: requiredText(),
    meetingStartTime: regexField(TIME_RE, 'validation.time'),
    meetingEndTime: regexField(TIME_RE, 'validation.time'),
    meetingLocation: requiredText(250, COLUMN_BYTES.meetingName),
  })
  .refine((v) => v.meetingStartTime < v.meetingEndTime, {
    path: ['meetingEndTime'],
    message: 'validation.timeRange',
  })
type EditValues = z.infer<typeof editSchema>

/** Seçili kişi kümesi başlangıçtakinden farklı mı? (sicil kümesi karşılaştırması) */
function setChanged(people: EmployeePick[], initial: ReadonlySet<string>): boolean {
  return people.length !== initial.size || people.some((p) => !initial.has(p.employeeId))
}

/** Detay katılımcısı → seçici (EmployeePick). firstName/lastName round-trip için detaydan gelir. */
function toPick(p: MeetingParticipant): EmployeePick {
  return {
    employeeId: p.employeeId,
    fullName: p.fullName,
    jobTitle: p.jobTitleTr,
    businessMail: p.businessMail,
    firstName: p.firstName,
    lastName: p.lastName,
    jobTitleTr: p.jobTitleTr,
    jobTitleEn: p.jobTitleEn,
    // Unvan grubu kodu TAŞINIR: düzenlemede yeni bir kişi eklenince liste istemcide yeniden
    // sıralanıyor ve kod olmasaydı mevcut katılımcıların tamamı "kodu yok" sayılıp sona düşerdi.
    employeeRoleGroupCode: p.employeeRoleGroupCode,
    // Liste etiketi de TAŞINIR (115): aynı gerekçe — düzenlemede geri gönderilmezse listeden gelen
    // katılımcıların bağı sessizce kopar.
    managerListId: p.managerListId ?? null,
  }
}

interface Props {
  detail: MeetingDetail
  onCancel: () => void
  onSaved: () => void
}

/**
 * Temel Bilgiler düzenleme kartı (044) — yalnız Özel Kalem rollerine ({@code MINUTES_CREATE}). Kalıcı alanlar:
 * ad/tarih/saat/yer + katılımcılar. Toplantı Grup Adı ve Tutanak Düzenleyen salt-okuma. Kaydet: optimistic lock
 * (bayat → 409, veri korunur); başarı → toast + detay tazelenir. <b>Aksiyonlar ve Tutanaklar bu kartın DIŞINDA</b>
 * (detay sayfasında hep düzenlenebilir, anında kaydeder) → burada gönderilmez (istek gövdesinde {@code actions}
 * yok → sunucu aksiyonlara dokunmaz; dosyalar ayrı uçtan). Gündem/Prensip/Karar görünür-pasif.
 */
export default function MeetingEditCard({ detail, onCancel, onSaved }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const update = useUpdateMeeting()

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useZodForm(editSchema, {
    defaultValues: {
      name: detail.name,
      // LocalDateTime "YYYY-MM-DDTHH:mm:ss" → tarih + başlangıç/bitiş saati.
      meetingDate: detail.meetingDate.slice(0, 10),
      meetingStartTime: detail.meetingDate.slice(11, 16),
      meetingEndTime: detail.meetingEndDate.slice(11, 16),
      meetingLocation: detail.meetingLocation,
    },
  })

  const [participants, setParticipants] = useState<EmployeePick[]>(detail.participants.map(toPick))
  const [participantError, setParticipantError] = useState<string | null>(null)
  // Dağıtım listesi (119). Yayınlanmış tutanakta yeni eklenen kişiye kayıtta PDF'li e-posta gider.
  const [distributionList, setDistributionList] = useState<EmployeePick[]>(
    (detail.distributionList ?? []).map(toPick),
  )
  const saving = update.isPending

  // Başlangıç katılımcı sicilleri — değişiklik (dirty) tespiti için sabit referans.
  const initialSicils = useMemo(
    () => new Set(detail.participants.map((p) => p.employeeId)),
    [detail.participants],
  )

  const initialDistributionSicils = useMemo(
    () => new Set((detail.distributionList ?? []).map((p) => p.employeeId)),
    [detail.distributionList],
  )

  // Herhangi bir kalıcı değişiklik var mı? (form alanları + katılımcı ve dağıtım listesi kümeleri.)
  const hasChanges = (): boolean =>
    isDirty ||
    setChanged(participants, initialSicils) ||
    setChanged(distributionList, initialDistributionSicils)

  const handleParticipantsChange = (next: EmployeePick[]) => {
    setParticipants(next)
    if (next.length > 0 && participantError) setParticipantError(null)
  }

  // İptal → değişiklik yoksa doğrudan çık; varsa onay iste (değişiklikler kaybolacak). Onaylanırsa okuma moduna dön.
  const requestCancel = () => {
    if (!hasChanges()) {
      onCancel()
      return
    }
    confirmDialog({
      header: t('meetings.detail.edit.cancelConfirm.title'),
      message: t('meetings.detail.edit.cancelConfirm.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('meetings.detail.edit.cancelConfirm.accept'),
      accept: onCancel,
    })
  }

  const onValid = (v: EditValues) => {
    if (participants.length === 0) {
      setParticipantError(t('minutes.participants.required'))
      return
    }
    update.mutate(
      {
        id: detail.id,
        body: {
          name: v.name,
          meetingDate: `${v.meetingDate}T${v.meetingStartTime}:00`,
          meetingEndDate: `${v.meetingDate}T${v.meetingEndTime}:00`,
          meetingLocation: v.meetingLocation,
          participants: participants.map(toPersonInput),
          // Her zaman gönderilir (boşsa []): sunucu null'ı "dokunma" sayar, [] ise listeyi temizler.
          distributionList: distributionList.map(toPersonInput),
          modDate: detail.modDate,
          // actions gönderilmez → sunucu aksiyonlara dokunmaz (aksiyonlar ayrı uçtan yönetiliyor).
        },
        // Dosyalar ayrı uçtan (Tutanaklar bölümü, anında) yönetilir → burada değişiklik yok.
        removeAttachmentIds: [],
        newFiles: [],
      },
      {
        onSuccess: () => {
          toast.success(t('meetings.detail.edit.success'))
          onSaved()
        },
        // Hata → global onError toast'lar (409 = bayat sürüm); kart açık kalır, veri korunur.
      },
    )
  }

  return (
    <>
      <ConfirmDialog />
      <form onSubmit={handleSubmit(onValid)} className="minutes-form" noValidate>
        <section className="minutes-section">
          <div className="minutes-section-head">
            <h2>{t('meetings.detail.info.title')}</h2>
            {/* İptal/Kaydet — Düzenle'nin yerinde (bu karta bağlı); ayrı alt bar UI'yı bozuyordu. */}
            <div className="mtg-edit-actions">
              <Button
                type="button"
                variant="secondary"
                size="small"
                label={t('meetings.detail.edit.cancel')}
                onClick={requestCancel}
                disabled={saving}
              />
              <Button
                type="submit"
                size="small"
                icon="pi pi-check"
                label={t('meetings.detail.edit.save')}
                disabled={saving}
              />
            </div>
          </div>
          <FormGrid>
            {/* Toplantı Grup Adı — salt-okuma (değiştirilemez). */}
            <FormField label={t('meetings.detail.info.group')} htmlFor="edit-group">
              <input
                id="edit-group"
                className="p-inputtext p-component p-disabled"
                value={detail.groupName ?? ''}
                disabled
                readOnly
              />
            </FormField>
            <FormTextField
              control={control}
              name="name"
              label={t('meetings.detail.info.name')}
              required
              maxLength={250}
            />
            <FormDateField control={control} name="meetingDate" label={t('meetings.detail.info.date')} required />
            <FormTimeRangeField
              control={control}
              startName="meetingStartTime"
              endName="meetingEndTime"
              label={t('minutes.info.time')}
              startPlaceholder={t('minutes.info.start')}
              endPlaceholder={t('minutes.info.end')}
              required
            />
            <FormTextField
              control={control}
              name="meetingLocation"
              label={t('meetings.detail.info.location')}
              required
              maxLength={250}
            />
            {/* Tutanak Düzenleyen — salt-okuma. */}
            <FormField label={t('meetings.detail.info.enteredUser')} htmlFor="edit-editor">
              <input
                id="edit-editor"
                className="p-inputtext p-component p-disabled"
                value={detail.enteredUserName ?? ''}
                disabled
                readOnly
              />
            </FormField>
          </FormGrid>

          {/* Katılımcılar (alt-başlık) — seçici + tablo (minutes ile ortak). */}
          <ParticipantsSection
            participants={participants}
            onChange={handleParticipantsChange}
            error={participantError}
            excludeIds={distributionList.map((p) => p.employeeId)}
          />
          {/* Dağıtım listesi katılımcıların hemen altında (119); iki liste birbirini dışlar. */}
          <ParticipantsSection
            participants={distributionList}
            onChange={setDistributionList}
            i18nPrefix="minutes.distributionList"
            required={false}
            inputId="edit-distribution-search"
            excludeIds={participants.map((p) => p.employeeId)}
          />
        </section>
      </form>
    </>
  )
}
